import { useCallback, useEffect, useRef, useState } from 'react';
import { industries, type Industry } from '../data/industries';

export function IndustriesCarousel() {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const count = industries.length;

  const go = useCallback(
    (next: number) => {
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  useEffect(() => {
    if (isPaused) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, 6000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, count]);

  const current: Industry = industries[index];

  return (
    <div
      className="industries-carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="industries-carousel-stage">
        <button
          type="button"
          className="industries-nav-btn industries-nav-prev"
          onClick={prev}
          aria-label="Previous industry"
        >
          <span className="material-symbols-outlined">chevron_left</span>
        </button>

        <div className="industries-slide-wrapper">
          <div className="industries-slide-active" key={current.id}>
            <div className="industries-slide-content">
              <div className="industries-slide-icon">
                <span className="material-symbols-outlined">{current.icon}</span>
              </div>
              <div className="industries-slide-copy">
                <span className="industries-slide-highlight">{current.highlight}</span>
                <h3>{current.name}</h3>
                <p>{current.blurb}</p>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="industries-nav-btn industries-nav-next"
          onClick={next}
          aria-label="Next industry"
        >
          <span className="material-symbols-outlined">chevron_right</span>
        </button>
      </div>

      <div className="industries-carousel-controls">
        <div className="industries-dots">
          {industries.map((ind, i) => (
            <button
              key={ind.id}
              type="button"
              className={`industries-dot${i === index ? ' is-active' : ''}`}
              onClick={() => go(i)}
              aria-label={`Go to ${ind.name}`}
              aria-current={i === index}
            >
              <span className="industries-dot-label">{ind.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
